import { tr } from "@/lib/translations";
import { useEffect, useRef, useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageCircle, Send, X } from "lucide-react";

const getSession = () => {
  let s = localStorage.getItem("islasol_chat");
  if (!s) { s = `chat-${crypto.randomUUID()}`; localStorage.setItem("islasol_chat", s); }
  return s;
};

const renderText = (text) =>
  text.split(/(\[[^\]]+\]\([^)]+\))/g).map((part, i) => {
    const m = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    return m ? <a key={i} href={m[2]} className="underline text-brand">{m[1]}</a> : <span key={i}>{part}</span>;
  });

const GREETING = {
  de: "Hola! Ich bin der privateHOLIDAY-Assistent. Fragen Sie mich nach Unterkünften, Inseln, Preisen oder der Buchung.",
  en: "Hola! I'm the privateHOLIDAY assistant. Ask me about properties, islands, prices or booking.",
  es: "¡Hola! Soy el asistente de privateHOLIDAY. Pregúntame por alojamientos, islas, precios o reservas.",
};

export const ChatWidget = () => {
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottom = useRef(null);
  const session = useRef(getSession());

  useEffect(() => {
    if (!open) return;
    api.get(`/chat/${session.current}`).then((r) => setMsgs(r.data.map((m) => ({ role: m.role, content: m.content })))).catch(() => {});
  }, [open]);
  useEffect(() => { bottom.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: session.current, message: text, lang }),
      });
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n\n"); buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6);
          if (data === "[DONE]") continue;
          const j = JSON.parse(data);
          const add = j.delta || (j.error ? `⚠ ${j.error}` : "");
          setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + add }; return c; });
        }
      }
    } catch {
      setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: "⚠ Verbindung fehlgeschlagen." }; return c; });
    } finally { setBusy(false); }
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} aria-label="Chat öffnen – Fragen zur Buchung" title="Chat öffnen" data-testid="chat-open-btn"
                className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-brand text-white shadow-xl hover:bg-brand-dark hover:scale-105 transition-all flex items-center justify-center">
          <MessageCircle className="w-6 h-6" />
        </button>
      )}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[360px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[calc(100vh-3rem)] bg-white rounded-2xl shadow-2xl border border-line flex flex-col overflow-hidden" data-testid="chat-widget">
          <div className="bg-brand text-white px-4 py-3 flex items-center justify-between">
            <div><div className="font-semibold text-sm">privateHOLIDAY Assistent</div><div className="text-[11px] opacity-80">KI · antwortet sofort</div></div>
            <button onClick={() => setOpen(false)} data-testid="chat-close-btn"><X className="w-5 h-5" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-page text-sm">
            <div className="bg-soft text-brand-deep rounded-2xl rounded-tl-sm px-3 py-2 max-w-[85%]">{tr(GREETING, lang)}</div>
            {msgs.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex"} data-testid={`chat-msg-${m.role}`}>
                <div className={m.role === "user" ? "bg-brand text-white rounded-2xl rounded-tr-sm px-3 py-2 max-w-[85%]"
                                                   : "bg-white border border-line rounded-2xl rounded-tl-sm px-3 py-2 max-w-[85%] whitespace-pre-wrap"}>
                  {m.content ? renderText(m.content) : <span className="animate-pulse">…</span>}
                </div>
              </div>
            ))}
            <div ref={bottom} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-line flex gap-2 bg-white">
            <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ihre Frage…" className="bg-white" data-testid="chat-input" />
            <Button type="submit" disabled={busy || !input.trim()} className="bg-brand hover:bg-brand-dark" data-testid="chat-send-btn"><Send className="w-4 h-4" /></Button>
          </form>
        </div>
      )}
    </>
  );
};
